import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  CreatePaymentTerminalRequest, PaymentProviderKind, PaymentTerminalConfigDto,
  PaymentTerminalDto, UpdatePaymentTerminalRequest
} from '../../../../../api/gen';

/**
 * The ZVT driver fields (driverKind / zvtHost / zvtPort) are already on the backend
 * PaymentTerminalConfigDto but not yet in the generated client (the operator-app OpenAPI spec predates
 * them). This local extension lets the form send them today; once `npm run generate:api:operator` is
 * re-run against the current backend they'll appear on the generated type and this can be dropped.
 */
export type TerminalDriverKind = 'Zvt' | 'WrpProprietary';

export interface PaymentTerminalConfigForm extends PaymentTerminalConfigDto {
  driverKind?: TerminalDriverKind;
  zvtHost?: string | null;
  zvtPort?: number | null;
}

@Component({
  selector: 'app-create-payment-terminal-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule
  ],
  templateUrl: './create-payment-terminal-form.component.html',
  styleUrl: './create-payment-terminal-form.component.scss'
})
export class CreatePaymentTerminalFormComponent implements OnInit {
  @Input() isProcessing = false;
  /**
   * Set to edit an existing terminal instead of creating one. The reader endpoint is the field that
   * actually moves in the field — a terminal on DHCP changed address three times in one hour during
   * the Medicent bring-up (2026-08-24) — and until this existed the only way to correct it was to
   * delete the terminal and recreate it, which mints a NEW terminal id and orphans every station
   * kiosk URL and signing key registered against the old one.
   */
  @Input() terminal: PaymentTerminalDto | null = null;
  @Output() createTerminal = new EventEmitter<CreatePaymentTerminalRequest>();
  @Output() updateTerminal = new EventEmitter<{ id: string; request: UpdatePaymentTerminalRequest }>();
  @Output() cancel = new EventEmitter<void>();

  get isEdit(): boolean { return !!this.terminal?.id; }

  // CardTerminal (Valina exit lane) and Im30 (self-service column) are both plain ZVT stations in the
  // unified design; Stripe is a hosted-checkout provider, not a terminal, so it's not offered here.
  readonly providerKinds: PaymentProviderKind[] = [PaymentProviderKind.CardTerminal, PaymentProviderKind.Im30];
  readonly driverKinds: TerminalDriverKind[] = ['Zvt', 'WrpProprietary'];

  // Limits mirror the backend DevicePolicy / PaymentTerminalPolicy so the client rejects the same inputs.
  readonly maxNameLength = 200;
  readonly maxDescriptionLength = 500;
  readonly maxProviderTerminalIdLength = 128;
  readonly maxLocationCodeLength = 64;
  readonly maxZvtHostLength = 253;

  terminalForm!: FormGroup;

  constructor(private fb: FormBuilder) {
    this.initForm();
  }

  ngOnInit(): void {
    if (this.terminal) {
      const cfg = this.terminal.config as PaymentTerminalConfigForm | undefined;
      this.terminalForm.patchValue({
        deviceName: this.terminal.deviceName ?? '',
        deviceDescription: this.terminal.deviceDescription ?? '',
        providerKind: cfg?.providerKind ?? PaymentProviderKind.CardTerminal,
        driverKind: (cfg?.driverKind as TerminalDriverKind) ?? 'Zvt',
        providerTerminalId: cfg?.providerTerminalId ?? '',
        locationCode: cfg?.locationCode ?? '',
        zvtHost: cfg?.zvtHost ?? '',
        zvtPort: cfg?.zvtPort ?? 20007
      });
      this.applyZvtValidators(this.isZvt);
    }
  }

  private initForm(): void {
    this.terminalForm = this.fb.group({
      // Required by PaymentTerminal.ValidateDeviceName.
      deviceName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(this.maxNameLength)]],
      providerKind: [PaymentProviderKind.CardTerminal, Validators.required],
      driverKind: ['Zvt' as TerminalDriverKind, Validators.required],
      // Required by PaymentTerminalConfig.ValidateProviderTerminalId + ValidateLocationCode.
      providerTerminalId: ['', [Validators.required, Validators.maxLength(this.maxProviderTerminalIdLength)]],
      locationCode: ['', [Validators.required, Validators.maxLength(this.maxLocationCodeLength)]],
      // Required only for a ZVT driver (ValidateZvtEndpoint) — validators toggled in applyZvtValidators().
      zvtHost: ['', [Validators.maxLength(this.maxZvtHostLength)]],
      zvtPort: [20007, [Validators.min(1), Validators.max(65535)]],
      // Optional — the backend models this as a non-nullable string (defaults to empty), never null.
      deviceDescription: ['', [Validators.maxLength(this.maxDescriptionLength)]]
    });

    // ZVT endpoint is required for the ZVT driver only; keep the validators in sync with the selected driver.
    this.applyZvtValidators(this.isZvt);
    this.terminalForm.get('driverKind')!.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((kind: TerminalDriverKind) => this.applyZvtValidators(kind === 'Zvt'));
  }

  private applyZvtValidators(isZvt: boolean): void {
    const host = this.terminalForm.get('zvtHost')!;
    const port = this.terminalForm.get('zvtPort')!;

    host.setValidators(isZvt
      ? [Validators.required, Validators.maxLength(this.maxZvtHostLength)]
      : [Validators.maxLength(this.maxZvtHostLength)]);
    port.setValidators(isZvt
      ? [Validators.required, Validators.min(1), Validators.max(65535)]
      : [Validators.min(1), Validators.max(65535)]);

    host.updateValueAndValidity({ emitEvent: false });
    port.updateValueAndValidity({ emitEvent: false });
  }

  get isZvt(): boolean {
    return this.terminalForm.get('driverKind')?.value === 'Zvt';
  }

  onSubmit(): void {
    if (this.terminalForm.invalid) {
      this.terminalForm.markAllAsTouched();
      return;
    }

    const v = this.terminalForm.value;
    const isZvt = v.driverKind === 'Zvt';

    const config: PaymentTerminalConfigForm = {
      providerKind: v.providerKind,
      driverKind: v.driverKind,
      // Both required server-side — sent as trimmed non-empty strings (never null).
      providerTerminalId: v.providerTerminalId?.trim(),
      locationCode: v.locationCode?.trim(),
      // The Pi reads these to reach the reader on the station LAN; only meaningful for a ZVT driver.
      zvtHost: isZvt ? v.zvtHost?.trim() : null,
      zvtPort: isZvt ? (v.zvtPort ?? null) : null
    };

    if (this.isEdit) {
      // Id is the one thing an update must never change: the station kiosk URL and the registered
      // Ed25519 signing key are both keyed on it. Send only the editable fields.
      this.updateTerminal.emit({
        id: this.terminal!.id!,
        request: {
          deviceName: v.deviceName?.trim(),
          deviceDescription: v.deviceDescription?.trim() || '',
          config
        } as UpdatePaymentTerminalRequest
      });
      return;
    }

    // deviceGroupId is filled in by the parent (it owns the group context). deviceDescription is a
    // non-nullable string on the backend, so an omitted description is an empty string, not null.
    const request: CreatePaymentTerminalRequest = {
      deviceGroupId: '',
      deviceName: v.deviceName?.trim(),
      deviceDescription: v.deviceDescription?.trim() || '',
      // Minted here, exactly as the barrier and camera forms do. This form used to omit the field
      // entirely, so the backend bound Guid.Empty and — because create-time validation skipped the
      // check — saved a terminal that then failed EVERY update and could never be edited again.
      // That is how Medicent's PT-302 ended up unrepairable through the API (2026-08-26). The
      // backend now rejects an all-zero id on create, so omitting it here would fail the request.
      deviceHardwareID: this.generateGuidValue(),
      config
    };

    this.createTerminal.emit(request);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  /**
   * Identity handle for the device row — any unique GUID will do; it is not read off the hardware.
   * Kept identical to the barrier/camera forms rather than using crypto.randomUUID(), which is only
   * available in a secure context.
   */
  private generateGuidValue(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
